import React, { useEffect, useState } from "react";
import { ChevronDown, ArrowRight } from "lucide-react";
import Navbar from "./navbar";
import Footer from "./footer";

function FAQ() {
  const [isVisible, setIsVisible] = useState(false);
  const [openIndex, setOpenIndex] = useState({});

  useEffect(() => {
    setIsVisible(true);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("appear");
          }
        });
      },
      { threshold: 0.1 }
    );
    
    document.querySelectorAll(".reveal").forEach((el) => {
      observer.observe(el);
    });
    
    return () => {
      document.querySelectorAll(".reveal").forEach((el) => {
        observer.unobserve(el);
      });
    };
  }, []);

  const toggleAccordion = (category, index) => {
    // Apply immediate visual feedback
    const buttonElement = document.getElementById(`btn-${category}-${index}`);
    if (buttonElement) {
      buttonElement.classList.add("clicked");
      setTimeout(() => {
        buttonElement.classList.remove("clicked");
      }, 300);
    }
    
    setOpenIndex((prev) => ({
      ...prev,
      [`${category}-${index}`]: !prev[`${category}-${index}`],
    }));
  };

  const generalQuestions = [
    {
      question: "What are jute products?",
      answer: "Jute products are eco-friendly items made from jute fiber, derived from the bark of the jute plant. Our collection includes bags, rugs, home decor, and packaging materials that are sustainable, biodegradable, and stylish."
    },
    {
      question: "Are jute products sustainable?",
      answer: "Yes! Jute is one of the most environmentally friendly fibers available. It's 100% biodegradable, naturally renewable, requires minimal water and pesticides to grow, and absorbs CO2 while releasing oxygen during its growth cycle."
    },
    {
      question: "How durable are jute products?",
      answer: "Jute products are known for their exceptional durability and strength. With proper care, our jute items can last for many years, making them a sustainable alternative to synthetic materials."
    }
  ];

  const orderingQuestions = [
    {
      question: "How do I place an order?",
      answer: "Browse our collection, select the items you wish to purchase, add them to your cart, and proceed to checkout. You can pay using various payment methods including credit/debit cards and digital wallets."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept major credit and debit cards (Visa, Mastercard, American Express), digital wallets (Google Pay, Apple Pay), and bank transfers. All payments are processed securely."
    },
    {
      question: "Do you ship internationally?",
      answer: "Yes, we ship our jute products worldwide. International shipping rates and delivery times vary depending on the destination country. Shipping details are calculated at checkout."
    }
  ];

  const returnsQuestions = [
    {
      question: "What is your return policy?",
      answer: "We offer a 7-day return policy on all our jute products. If you're not completely satisfied with your purchase, you can return it within 7 days of delivery for a full refund or exchange."
    },
    {
      question: "How do I initiate a return?",
      answer: (
        <>
          To initiate a return, contact our customer service team via WhatsApp at{" "}
          <a
            href="https://wa.me/+918398862901"
            // className="text-amber-600 hover:text-amber-700 underline transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            +91 8398862901
          </a>{" "}
          or email with your order number and reason for return. We'll guide you through the process and provide a return shipping label if applicable.
        </>
      )
    },
    {
      question: "Are there any items that cannot be returned?",
      answer: "Custom-made jute products, personalized items, and products marked as final sale cannot be returned unless they arrive damaged or defective."
    }
  ];

  const careQuestions = [
    {
      question: "How should I care for my jute products?",
      answer: "To maintain the quality of your jute products, keep them dry and clean. Spot clean with a damp cloth and mild soap when necessary. Avoid machine washing or soaking in water. For jute rugs, regular vacuuming and immediate spot cleaning of spills is recommended."
    },
    {
      question: "Can jute products be used outdoors?",
      answer: "Jute products are primarily designed for indoor use as prolonged exposure to moisture, direct sunlight, and extreme weather can cause deterioration. For outdoor use, we recommend bringing them indoors when not in use."
    },
    {
      question: "How can I remove stains from jute products?",
      answer: "For minor stains, immediately blot (don't rub) with a clean cloth. For tougher stains, lightly dampen a cloth with a mixture of mild soap and water, gently dab the stain, and air dry completely away from direct heat or sunlight."
    }
  ];

  const categories = [
    { id: "general", title: "General Questions", questions: generalQuestions },
    { id: "ordering", title: "Ordering & Shipping", questions: orderingQuestions },
    { id: "returns", title: "Returns & Refunds", questions: returnsQuestions },
    { id: "care", title: "Product Care", questions: careQuestions },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white text-gray-800">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden pt-24 pb-16 bg-gradient-to-b from-amber-50 to-white">
        <div className="container mx-auto px-4">
          <div className="relative z-10 text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-amber-600 to-amber-800 reveal">
              How can we help?
            </h1>
            <p className="text-gray-700 text-xl mb-10 reveal">
              Find answers to common questions about our sustainable jute products
            </p>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-24 left-10 w-64 h-64 bg-amber-100 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob pointer-events-none"></div>
        <div className="absolute bottom-10 right-10 w-64 h-64 bg-amber-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000 pointer-events-none"></div>
      </div>
      
      {/* FAQ Content */}
      <div className="flex-grow py-12">
        <div className="container mx-auto px-4">
          {categories.map((category) => (
            <div key={category.id} className="mb-12 reveal">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-8 relative inline-block">
                {category.title}
                <span className="absolute bottom-0 left-0 w-1/2 h-1  rounded-full"></span>
              </h2>
              
              <div className="space-y-4">
                {category.questions.map((faq, index) => (
                  <div
                    key={index}
                    className={`border border-gray-100 rounded-xl overflow-hidden transition-all duration-300 ease-out ${
                      openIndex[`${category.id}-${index}`] 
                        ? "shadow-lg bg-white" 
                        : "shadow-sm bg-white"
                    }`}
                  >
                    <button
                      id={`btn-${category.id}-${index}`}
                      className="accordion-btn w-full text-left p-6 flex justify-between items-center focus:outline-none transition-colors duration-150  "
                      onClick={() => toggleAccordion(category.id, index)}
                    >
                      <span className="text-lg font-medium text-gray-800 pr-8">
                        {faq.question}
                      </span>
                      <span className={`text-amber-600 flex-shrink-0 transition-transform duration-200 ease-out ${
                        openIndex[`${category.id}-${index}`] ? "rotate-180" : "rotate-0"
                      }`}>
                        <ChevronDown className="w-6 h-6" />
                      </span>
                    </button>
                    
                    <div 
                      className={`accordion-content transition-all duration-300 ease-out overflow-hidden ${
                        openIndex[`${category.id}-${index}`] ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                      }`}
                      style={{
                        transitionProperty: "max-height, opacity, padding",
                        transitionDuration: openIndex[`${category.id}-${index}`] ? "300ms, 150ms, 150ms" : "200ms, 200ms, 100ms",
                        transitionDelay: openIndex[`${category.id}-${index}`] ? "0ms, 0ms, 0ms" : "0ms, 0ms, 0ms",
                        paddingTop: openIndex[`${category.id}-${index}`] ? "16px" : "0px",
                        paddingBottom: openIndex[`${category.id}-${index}`] ? "24px" : "0px",
                        paddingLeft: "24px",
                        paddingRight: "24px"
                      }}
                    >
                      <div className="text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Contact CTA Section */}
      <div className="bg-gradient-to-r from-amber-50 to-amber-100 py-16">
        <div className="container mx-auto px-4 text-center reveal">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Couldn't find what you're looking for?
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Our customer support team is here to help with any questions you may have about our sustainable jute products.
          </p>
          <a 
            href="https://wa.me/+918398862901" 
            className="inline-flex items-center px-6 py-3 bg-amber-600 text-white rounded-full  transition-all duration-150"
          >
            Contact us
            <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
          </a>
        </div>
      </div>
      
      <Footer />
      
      <style jsx>{`
        @keyframes blob {
          0% { transform: scale(1) translate(0px, 0px); }
          33% { transform: scale(1.1) translate(30px, -30px); }
          66% { transform: scale(0.9) translate(-30px, 30px); }
          100% { transform: scale(1) translate(0px, 0px); }
        }
        
        .animate-blob {
          animation: blob 18s infinite cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        .reveal {
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        .reveal.appear {
          opacity: 1;
          transform: translateY(0);
        }
        
        .accordion-btn.clicked {
          background-color: rgba(251, 191, 36, 0.2);
          transform: scale(0.99);
          transition: background-color 0.1s ease, transform 0.1s ease;
        }
        
        
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        /* Ripple effect */
        .accordion-btn {
          position: relative;
          overflow: hidden;
        }

        .accordion-btn::after {
          content: '';
          position: absolute;
          width: 100px;
          height: 100px;
          background: rgba(255, 255, 255, 0.7);
          border-radius: 50%;
          transform: scale(0);
          opacity: 0;
          pointer-events: none;
          transition: transform 0.5s, opacity 0.5s;
        }

        .accordion-btn.clicked::after {
          animation: ripple 0.6s ease-out;
        }

        @keyframes ripple {
          0% {
            transform: scale(0);
            opacity: 0.5;
          }
          100% {
            transform: scale(15);
            opacity: 0;
          }
        }
      `}</style>

      {/* Add JavaScript for ripple effect */}
      <script dangerouslySetInnerHTML={{
        __html: `
          document.addEventListener('DOMContentLoaded', () => {
            document.querySelectorAll('.accordion-btn').forEach(button => {
              button.addEventListener('click', function(e) {
                const x = e.clientX - this.getBoundingClientRect().left;
                const y = e.clientY - this.getBoundingClientRect().top;
                
                const rippleEffect = document.createElement('span');
                rippleEffect.className = 'ripple';
                rippleEffect.style.left = x + 'px';
                rippleEffect.style.top = y + 'px';
                
                this.appendChild(rippleEffect);
                
                setTimeout(() => {
                  rippleEffect.remove();
                }, 600);
              });
            });
          });
        `
      }} />
    </div>
  );
}

export default FAQ;